import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// Define filter state interface
export interface UsersFilterState {
  enabled: 'all' | 'enabled' | 'disabled';
  username: string;
  activeFilters: string[];
}

// Define hook interface
interface UseUsersFilter {
  filterState: UsersFilterState;
  tempState: UsersFilterState;
  filterDrawerOpen: boolean;
  expandedAccordions: string[];
  setFilterDrawerOpen: (open: boolean) => void;
  setExpandedAccordions: (expanded: string[]) => void;
  handleEnabledChange: (value: 'all' | 'enabled' | 'disabled') => void;
  handleUsernameFilterChange: (value: string) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  removeFilter: (filter: string) => void;
}

// Helper to parse URL params into filter state
const parseUrlParams = (searchParams: URLSearchParams): Omit<UsersFilterState, 'activeFilters'> => {
  const enabled = searchParams.get('status') as 'all' | 'enabled' | 'disabled' | null;
  const username = searchParams.get('search') || '';

  return {
    // Default to 'enabled' if no status param (matches original default behavior)
    enabled: enabled || 'enabled',
    username
  };
};

// Helper to build active filters array from state
const buildActiveFilters = (state: Omit<UsersFilterState, 'activeFilters'>): string[] => {
  const activeFilters: string[] = [];

  if (state.enabled !== 'all') {
    activeFilters.push(`Status: ${state.enabled}`);
  }

  if (state.username && state.username.trim() !== '') {
    activeFilters.push(`Username: ${state.username}`);
  }

  return activeFilters;
};

// Custom hook for user filters
const useUsersFilter = (): UseUsersFilter => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initialState = parseUrlParams(searchParams);
  const initialActiveFilters = buildActiveFilters(initialState);

  // State for filter drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);

  // State for filter accordions
  const [expandedAccordions, setExpandedAccordions] = useState<string[]>(['status', 'username']);

  // State for applied filters
  const [filterState, setFilterState] = useState<UsersFilterState>({
    ...initialState,
    activeFilters: initialActiveFilters
  });

  // Temporary state for filters that are not yet applied
  const [tempState, setTempState] = useState<UsersFilterState>({
    ...initialState,
    activeFilters: initialActiveFilters
  });

  // Sync URL params when filter state changes
  const updateUrlParams = useCallback((state: UsersFilterState) => {
    const params = new URLSearchParams(searchParams);

    // Update status param (only if not default 'enabled')
    if (state.enabled !== 'enabled') {
      params.set('status', state.enabled);
    } else {
      params.delete('status');
    }

    // Update search/username param
    if (state.username && state.username.trim() !== '') {
      params.set('search', state.username.trim());
    } else {
      params.delete('search');
    }

    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Handle enabled status change
  const handleEnabledChange = useCallback((value: 'all' | 'enabled' | 'disabled') => {
    setTempState(prev => ({
      ...prev,
      enabled: value
    }));
  }, []);

  // Handle username filter change
  const handleUsernameFilterChange = useCallback((value: string) => {
    setTempState(prev => ({
      ...prev,
      username: value
    }));
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    const activeFilters = buildActiveFilters(tempState);

    const newFilterState: UsersFilterState = {
      ...tempState,
      activeFilters
    };

    // Update filter state
    setFilterState(newFilterState);
    updateUrlParams(newFilterState);

    // Close the filter drawer
    setFilterDrawerOpen(false);
  }, [tempState, updateUrlParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    const defaultState: UsersFilterState = {
      enabled: 'enabled',
      username: '',
      activeFilters: ['Status: enabled']
    };

    // Reset temporary state
    setTempState(defaultState);

    // Reset filter state
    setFilterState(defaultState);
    updateUrlParams(defaultState);

    // Close the filter drawer
    setFilterDrawerOpen(false);
  }, [updateUrlParams]);

  // Remove a specific filter
  const removeFilter = useCallback((filter: string) => {
    // Create a new state based on the filter to remove
    const newState = { ...filterState };
    const newActiveFilters = newState.activeFilters.filter(f => f !== filter);

    if (filter.startsWith('Status:')) {
      newState.enabled = 'all';
    } else if (filter.startsWith('Username:')) {
      newState.username = '';
    }

    const updatedState: UsersFilterState = {
      ...newState,
      activeFilters: newActiveFilters
    };

    // Update filter state
    setFilterState(updatedState);
    updateUrlParams(updatedState);

    // Update temporary state
    setTempState(updatedState);
  }, [filterState, updateUrlParams]);

  // Sync tempState with filterState when filterState changes
  useEffect(() => {
    setTempState({
      ...filterState
    });
  }, [filterState]);

  return {
    filterState,
    tempState,
    filterDrawerOpen,
    expandedAccordions,
    setFilterDrawerOpen,
    setExpandedAccordions,
    handleEnabledChange,
    handleUsernameFilterChange,
    applyFilters,
    clearFilters,
    removeFilter
  };
};

export default useUsersFilter;
